import React, { useState, useEffect, useContext, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { ArrowBack, ChevronRight } from '@mui/icons-material';

import GoalInfo from './GoalInfo';
import SingleIndicatorDetails from './SingleIndicatorDetails';
import { StatusIndicator } from './StatusIndicator';
import SDGSpinner from './SDGSpinner';

import { GoalsContext } from '../State/GoalsContext.ts';
import { IndicatorsContext } from '../State/IndicatorsContext.ts';
import { normalizeGoalType, goalTypeToUrl } from '../utils/goalTypeUtils';

const AllGoalIndicatorsMobile = () => {
  const { goalType: urlGoalType, goalId, indicatorId } = useParams();
  const goalType = normalizeGoalType(urlGoalType);
  const navigate = useNavigate();

  const { fetchGoal, selectedGoal, loading: goalLoading } = useContext(GoalsContext);
  const {
    fetchAllIndicatorsByGoal,
    fetchSingleIndicator,
    indicators,
    loading: indicatorsLoading
  } = useContext(IndicatorsContext);

  const [selectedIndicatorId, setSelectedIndicatorId] = useState(indicatorId || null);
  const [view, setView] = useState(indicatorId ? 'detail' : 'list'); // 'list' or 'detail'
  const [fullIndicatorData, setFullIndicatorData] = useState(null);

  // Scroll to top when opening indicators view or when goal/indicator changes (mobile)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [goalId, indicatorId]);

  useEffect(() => {
    const parsedGoalId = parseInt(goalId);
    console.log('📱 AllGoalIndicatorsMobile goal useEffect:', { 
      goalId: parsedGoalId, 
      goalType,
      selectedGoalId: selectedGoal?.id,
      shouldFetch: !selectedGoal || selectedGoal.id !== parsedGoalId
    });
    
    if (goalId && (!selectedGoal || selectedGoal.id !== parsedGoalId)) {
      console.log('📱 Fetching goal:', parsedGoalId);
      fetchGoal(parsedGoalId);
    }
    // Removed goalType from dependencies to prevent unnecessary re-fetches
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalId, fetchGoal, selectedGoal?.id]);

  // Use ref to track if we've already fetched for this goalId+goalType combo
  const fetchedRef = useRef({ goalId: null, goalType: null });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const isLoadingRef = useRef(false);
  
  useEffect(() => {
    const parsedGoalId = parseInt(goalId);
    const shouldFetch = goalId && goalType && 
      (fetchedRef.current.goalId !== parsedGoalId || fetchedRef.current.goalType !== goalType) &&
      !isLoadingRef.current;
    
    if (shouldFetch) {
      console.log('📱 AllGoalIndicatorsMobile indicators useEffect:', { 
        goalId: parsedGoalId, 
        goalType,
        shouldFetch
      });
      fetchedRef.current = { goalId: parsedGoalId, goalType };
      isLoadingRef.current = true;
      setIsInitialLoad(true);
      // Use a small delay to ensure loading state is visible, then check if fetch completed
      fetchAllIndicatorsByGoal(parsedGoalId, goalType).then(() => {
        // Wait a bit for state to update, then clear initial load
        setTimeout(() => {
          setIsInitialLoad(false);
          isLoadingRef.current = false;
        }, 100);
      }).catch(() => {
        setIsInitialLoad(false);
        isLoadingRef.current = false;
      });
    } else if (!goalId || !goalType) {
      // If we don't need to fetch, wait a moment to ensure indicators are loaded
      setTimeout(() => {
        setIsInitialLoad(false);
        isLoadingRef.current = false;
      }, 100);
    }
    // Removed fetchAllIndicatorsByGoal from dependencies to prevent re-fetches
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalId, goalType]);

  useEffect(() => {
    if (indicatorId) {
      setSelectedIndicatorId(parseInt(indicatorId));
      setView('detail');
    } else {
      setView('list');
    }
  }, [indicatorId]);

  const selectedIndicator = useMemo(() => {
    return indicators.find((i) => i.id === parseInt(selectedIndicatorId)) || null;
  }, [selectedIndicatorId, indicators]);

  // Prevent refetch loops when fetchSingleIndicator updates IndicatorsContext state
  const lastFetchedIndicatorRef = useRef(null);

  // Always fetch full indicator preview when selected to ensure we have latest assessment data
  useEffect(() => {
    if (selectedIndicatorId) {
      const fetchKey = `${selectedIndicatorId}-${goalType}`;
      if (lastFetchedIndicatorRef.current === fetchKey) return;
      lastFetchedIndicatorRef.current = fetchKey;

      // Always fetch fresh preview data to ensure we have the latest assessment
      fetchSingleIndicator(parseInt(selectedIndicatorId), goalType).then(fullData => {
        if (fullData) {
          setFullIndicatorData(fullData);
        } else {
          // Fallback to indicator from list if preview fetch fails
          if (selectedIndicator) setFullIndicatorData(selectedIndicator);
        }
      });
    } else {
      setFullIndicatorData(null);
      lastFetchedIndicatorRef.current = null;
    }
  }, [selectedIndicatorId, goalType, fetchSingleIndicator, selectedIndicator]);

  const handleIndicatorClick = useCallback((indicator) => {
    // Prevent rapid clicks
    if (isInitialLoad) return;
    
    setSelectedIndicatorId(indicator.id);
    setView('detail');
    navigate(`/${goalTypeToUrl(goalType)}/goal/${goalId}/indicators/${indicator.id}`);
  }, [goalId, goalType, navigate, isInitialLoad]);

  const handleBackToList = useCallback(() => {
    setView('list');
    setSelectedIndicatorId(null);
    setFullIndicatorData(null);
    navigate(`/${goalTypeToUrl(goalType)}/goal/${goalId}/indicators`);
  }, [goalId, goalType, navigate]);

  const handleBackToGoals = useCallback(() => {
    navigate(`/${goalTypeToUrl(goalType)}`);
  }, [goalType, navigate]);

  // Show loading spinner while fetching goal or indicators, or if goal is not loaded yet
  // Also show spinner during initial load to prevent blank page
  if (goalLoading || indicatorsLoading || !selectedGoal || isInitialLoad) {
    return <SDGSpinner size="lg" text="Loading indicators..." />;
  }

  if (!selectedGoal) return <p>Goal not found</p>;

  return (
    <div className="mobile-indicators-container">
      {view === 'list' && (
        <>
          <div className="mobile-header">
            <Button
              variant="link"
              onClick={handleBackToGoals}
              className="mobile-back-btn"
            >
              <ArrowBack fontSize="small" /> Back to Goals
            </Button>
          </div>

          <div className="mobile-goal-info">
            <GoalInfo goal={selectedGoal} />
          </div>

          {/* Indicators List */}
          <div className="mobile-section">
            <div className="mobile-section-header">
              <h5>Indicators ({indicators.length})</h5>
            </div>
            
            <div className="mobile-list">
              {indicators.map((indicator) => (
                <div 
                  key={indicator.id}
                  className="mobile-list-item"
                  onClick={() => handleIndicatorClick(indicator)}
                >
                  <div className="mobile-list-content">
                    <div className="mobile-list-main">
                      <div className="mobile-list-title">
                        <strong>{indicator.acf.index}</strong>
                        {' - '}
                        <span>{indicator.title || indicator.title_render?.replace(/^\d+(?:\.[0-9a-z]+)*\s*[·-]\s*/i, '') || ''}</span>
                      </div>
                    </div>
                    <div className="mobile-list-status">
                      <StatusIndicator status={indicator.acf.status} />
                    </div>
                    <div className="mobile-list-arrow">
                      <ChevronRight />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {view === 'detail' && (fullIndicatorData || selectedIndicator) && (
        <>
          <div className="mobile-header">
            <Button
              variant="link"
              onClick={handleBackToList}
              className="mobile-back-btn"
            >
              <ArrowBack fontSize="small" /> Back to Indicators
            </Button>
          </div>

          {/* Indicator Detail */}
          <div className="mobile-detail-content">
            <SingleIndicatorDetails indicator={fullIndicatorData || selectedIndicator} />
          </div>
        </>
      )}
    </div>
  );
};

export default AllGoalIndicatorsMobile;
